import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Menu } from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';

/**
 * A button that opens a dropdown MUI `Menu` of caller-provided items,
 * anchored below the button. Use as the base for any "button with menu
 * choices" control (e.g. the broadcast and action-alert create buttons).
 */
const ButtonMenu = ({
  children,
  className,
  color,
  disabled,
  endIcon,
  name,
  onOpen,
  variant,
  startIcon,
}) => {
  const [anchorSettingsElement, setAnchorSettingsElement] = useState(null);

  const handleSettingsClick = event => {
    setAnchorSettingsElement(event.currentTarget);

    if (onOpen) onOpen();
  };
  const handleSettingsClose = () => setAnchorSettingsElement(null);

  return (
    <>
      <Button
        aria-controls={name}
        aria-haspopup="true"
        className={className}
        color={color}
        disableElevation
        disabled={disabled}
        endIcon={endIcon}
        onClick={handleSettingsClick}
        startIcon={startIcon}
        variant={variant}
      >
        {name}
      </Button>
      <Menu
        anchorEl={anchorSettingsElement}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        transformOrigin={{
          horizontal: 'center',
          vertical: 'top',
        }}
        id={name}
        keepMounted
        onClose={handleSettingsClose}
        open={Boolean(anchorSettingsElement)}
      >
        <div>{typeof children === 'function' ? children(handleSettingsClose) : children}</div>
      </Menu>
    </>
  );
};

ButtonMenu.defaultProps = {
  className: '',
  color: 'inherit',
  disabled: false,
  endIcon: null,
  name: 'Settings',
  onOpen: null,
  startIcon: <SettingsIcon />,
  variant: 'outlined',
};

ButtonMenu.propTypes = {
  /** Menu contents; the function form is called with the menu-close handler so items can close the menu themselves. */
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.arrayOf(PropTypes.node), PropTypes.node])
    .isRequired,
  className: PropTypes.string,
  color: PropTypes.string,
  disabled: PropTypes.bool,
  endIcon: PropTypes.node,
  /** Button label; also used as the menu's DOM id and aria-controls value. */
  name: PropTypes.string,
  /** Called when the menu is opened (e.g. to lazily fetch data for the items). */
  onOpen: PropTypes.func,
  startIcon: PropTypes.node,
  variant: PropTypes.string,
};

export default ButtonMenu;
